import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { View, Image, Text, StyleSheet } from 'react-native';
import { useSelector } from 'react-redux';
import { buildAvatarUrl } from '../utilities/avatarUrl';
import config from '../config';
import Icon from './Icon';
import type { RootState } from '../store';

// Deterministic background colour derived from the user's initials — same
// palette the web app's react-avatar library uses for initials avatars.
const PALETTE = [
  '#5B8CBA', '#6B7FBA', '#7B6FBA', '#8B5FBA', '#9B50AB',
  '#A5505A', '#A56050', '#A57050', '#A58050', '#5BA580',
  '#50A58B', '#50A5A5', '#5090A5', '#5070A5', '#5050A5',
];

export function initialsColor(initials: string): string {
  let code = 0;
  for (let i = 0; i < initials.length; i++) code += initials.charCodeAt(i);
  return PALETTE[code % PALETTE.length];
}

// ── Module-level avatar failure cache ───────────────────────────────────────
// Without this, every UserAvatar mount runs through MAX_ATTEMPTS network
// requests independently. A user navigating around the app remounts the same
// avatar (chat header, project list, conversation rows, etc.) over and over,
// and each remount kicks off another retry burst — easily hundreds of failed
// requests for a single broken (or genuinely empty 204) avatar over a session.
//
// We key on the full URL — which already encodes the user's `updated_at`
// timestamp via `buildAvatarUrl`, so `BUMP_AVATAR_REFRESH` (which bumps that
// timestamp in Redux) automatically produces a new URL the cache hasn't seen
// and triggers a fresh fetch. The TTL guards against a transient backend blip
// permanently locking the avatar to initials for the entire session.
const FAILURE_TTL_MS = 5 * 60 * 1000;
const failedAvatarUrls = new Map<string, number>();

function isAvatarFailed(url: string): boolean {
  const t = failedAvatarUrls.get(url);
  if (!t) return false;
  if (Date.now() - t > FAILURE_TTL_MS) {
    failedAvatarUrls.delete(url);
    return false;
  }
  return true;
}

function markAvatarFailed(url: string): void {
  failedAvatarUrls.set(url, Date.now());
}

// ── Retry tuning ───────────────────────────────────────────────────────────
// Hoisted out of the component so they aren't re-allocated on every render.
// Schedule was tuned for the lock-screen path (see comment on `error` state
// inside the component): the first retry is fast enough to recover from a
// blip, the second sits past the typical Touch ID prompt duration so we
// re-issue once focus returns. Jitter (±25%) spreads the retry herd when many
// avatars (e.g. a contacts list) all error at once during a transient backend
// hiccup — without it they'd retry in lockstep and hammer the backend just as
// it's recovering.
const MAX_ATTEMPTS = 3; // initial + 2 retries — caps the request fan-out.
const RETRY_BACKOFF_MS = [800, 2400];
const RETRY_JITTER = 0.25;

interface UserAvatarProps {
  cognitoId?: string | null;
  /** Contact record ID — used to load avatar from /contacts/get-contact-avatar/{id} */
  contactId?: number | string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** Boolean flag stored in Redux — false means skip the network request */
  hasAvatar?: boolean;
  updatedAt?: string | null;
  size?: number;
  style?: object;
  /** Local base64 data URI — overrides remote URL (used during avatar editing) */
  localUri?: string | null;
}

export default function UserAvatar({
  cognitoId,
  contactId,
  firstName,
  lastName,
  hasAvatar = false,
  updatedAt,
  size = 28,
  style,
  localUri,
}: UserAvatarProps) {
  // `error` latches only after we've exhausted retries — a single transient
  // failure (slow network, lambda cold start, brief 5xx, focus-loss when an
  // OS prompt like Touch ID takes over) won't lock the avatar to initials
  // forever. `attempt` participates in the Image key so the retry forces a
  // fresh load. (Retry schedule constants are hoisted above the component.)
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Mirror of `attempt` for the error handler to read synchronously without
  // resorting to the `setState(prev => prev)` peek pattern. Kept in lockstep
  // with the state via the reset effect and the timer callback.
  const attemptRef = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshTs = useSelector((state: RootState) =>
    cognitoId ? state.session.avatarTimestamps?.[cognitoId] : undefined
  );
  const effectiveUpdatedAt = refreshTs ? String(refreshTs) : updatedAt;
  // Only fire the remote avatar request if the caller explicitly signals one exists,
  // preventing 404s for every user who has never uploaded an avatar.
  const remoteUrl = (hasAvatar && cognitoId) ? buildAvatarUrl(cognitoId, effectiveUpdatedAt) : null;
  const contactUrl = !cognitoId && contactId
    ? `${config.apiGateway.endpoint}/contacts/get-contact-avatar/${contactId}${effectiveUpdatedAt ? `?${effectiveUpdatedAt}` : ''}`
    : null;
  // `baseUrl` is the stable identity used for resetting state when the
  // source actually changes (e.g. user switches accounts, avatar is
  // refreshed via BUMP_AVATAR_REFRESH). `imageUrl` is what we hand to
  // <Image>; on retry we suffix `_r=N` so the browser HTTP cache can't
  // serve the previously-failed response back to us. Without this the
  // React `key` change remounts the DOM `<img>` but the network layer
  // returns the same cached error and we re-trigger onError immediately.
  const baseUrl = localUri || remoteUrl || contactUrl;
  // If we've already exhausted retries for this exact URL in a previous
  // mount (recently — TTL-bounded), short-circuit to initials immediately
  // instead of re-flooding the backend. Skipped for `localUri` since those
  // are local data URIs that don't fail intermittently.
  const cachedFail = !!baseUrl && !localUri && isAvatarFailed(baseUrl);
  const imageUrl = useMemo(() => {
    if (!baseUrl) return null;
    if (attempt === 0 || localUri) return baseUrl;
    const sep = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${sep}_r=${attempt}`;
  }, [baseUrl, attempt, localUri]);

  // Reset retry state whenever the underlying source changes. Keyed on
  // `baseUrl` (not the suffixed `imageUrl`) so bumping `attempt` during a
  // retry doesn't immediately wipe the in-flight retry state. We pre-set
  // `error=true` if the URL is in the failure cache so we never even render
  // the <Image> for a known-broken URL.
  useEffect(() => {
    if (retryTimer.current) { clearTimeout(retryTimer.current); retryTimer.current = null; }
    if (baseUrl) {
      const known = !localUri && isAvatarFailed(baseUrl);
      setError(known);
      setAttempt(0);
      attemptRef.current = 0;
    }
  }, [baseUrl, localUri]);

  useEffect(() => () => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
  }, []);

  const handleImageError = useCallback(() => {
    // Retry up to MAX_ATTEMPTS times before falling back to initials. Without
    // this, any transient image-load failure (cold-start lambda above api-gw
    // timeout, brief network blip, RN-Web racing the <img> teardown on key
    // change, OS prompts like Touch ID stealing focus from the renderer mid-
    // request) leaves the avatar stuck on initials until the URL changes
    // again — even though a refetch a moment later would succeed. The
    // backoff escalates so the second retry lands past the typical OS prompt
    // duration (~2s) without making real failures feel slow.
    //
    // Clear any pending retry first so a rapid second `onError` (e.g. RN-Web
    // briefly mounting two `<img>` elements during a key swap) doesn't queue
    // a duplicate timer — we always honour the latest failure.
    if (retryTimer.current) { clearTimeout(retryTimer.current); retryTimer.current = null; }
    const current = attemptRef.current;
    if (current + 1 >= MAX_ATTEMPTS) {
      // Final failure — record in the module-level cache so future remounts
      // of any UserAvatar pointing at this URL skip the network entirely.
      if (baseUrl && !localUri) markAvatarFailed(baseUrl);
      setError(true);
      return;
    }
    const base = RETRY_BACKOFF_MS[current] ?? RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1];
    const jitter = base * RETRY_JITTER * (Math.random() * 2 - 1);
    retryTimer.current = setTimeout(() => {
      retryTimer.current = null;
      attemptRef.current = current + 1;
      setAttempt(current + 1);
    }, base + jitter);
  }, [baseUrl, localUri]);

  const initials =
    `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  // Used purely to derive a stable colour for the placeholder circle when we
  // have no initials to show.
  const colorSeed = initials || cognitoId || String(contactId ?? '') || 'user';

  // Cached failure short-circuits even the first render — without it, every
  // remount would fire one stray request before the effect could mark the
  // component errored.
  const showImage = !!imageUrl && !error && !cachedFail;
  const radius = size / 2;
  const initialsFontSize = Math.min(18, Math.max(7, Math.round(size * 0.38)));
  const initialsLetterSpacing = size <= 18 ? 0.2 : 0.5;
  const iconSize = Math.max(8, Math.round(size * 0.5));

  return (
    <View
      style={[
        styles.wrapper,
        { width: size, height: size, borderRadius: radius },
        style,
      ]}
    >
      {showImage ? (
        <Image
          // `imageUrl` already varies per attempt (`_r=N` suffix on retries),
          // which both forces a fresh network request and gives RN-Web a new
          // identity so it remounts the `<img>`. We don't need a separate
          // `attempt` token in the key — the URL change handles both layers.
          key={imageUrl as string}
          source={{ uri: imageUrl as string }}
          style={{ width: size, height: size, borderRadius: radius }}
          onError={handleImageError}
        />
      ) : (
        <View
          style={[
            styles.initials,
            {
              width: size,
              height: size,
              borderRadius: radius,
              backgroundColor: initialsColor(colorSeed),
            },
          ]}
        >
          {initials ? (
            <Text style={[styles.initialsText, { fontSize: initialsFontSize, letterSpacing: initialsLetterSpacing }]}>
              {initials}
            </Text>
          ) : (
            <Icon icon={['fas', 'user']} size={iconSize} color="#fff" />
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    overflow: 'hidden',
    flexShrink: 0,
  },
  initials: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  initialsText: {
    color: '#fff',
    fontWeight: '600',
    letterSpacing: 0.5,
  },
});
